import { Auth as SupabaseAuth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Building2, MapPin, Users, TrendingUp, Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { routeForRole } from "@/lib/auth/roleRedirect";
import { captureAuthError } from "@/lib/sentry";
import LoadingSpinner from "@/components/LoadingSpinner";
import logo from "@/assets/logo-khaylani-new.png";

const Auth = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    const checkUserAndRedirect = async (session: any) => {
      if (!session) return;
      
      setIsRedirecting(true);

      // SECURITY: Check app_metadata for superadmin role
      const metadataRole = session.user.app_metadata?.role;
      if (metadataRole === 'superadmin') {
        navigate("/superadmin");
        return;
      }

      // For all other users, fetch their role from user_roles
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .maybeSingle();

      const role = roleData?.role;

      // Redirect based on role
      try {
        const redirectPath = routeForRole(role);
        navigate(redirectPath);
      } catch (error) {
        // Fallback to dashboard if role is invalid
        console.error('Invalid role or redirect error:', error);
        captureAuthError(session.user.id, role, error);
        navigate("/dashboard");
      }
    };
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        checkUserAndRedirect(session);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session) {
        setTimeout(() => {
          void checkUserAndRedirect(session);
        }, 0);
      }
      if (event === "PASSWORD_RECOVERY") {
        navigate("/reset-password");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Track timing to normalize response and prevent email enumeration
    const startTime = Date.now();

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        // Swallow error to prevent information leakage
        console.debug('Password reset error (hidden from user)');
      }
    } catch (error: any) {
      // Swallow all errors to prevent information leakage
      console.debug('Password reset exception (hidden from user)');
    }

    // Normalize timing to at least 500ms to prevent timing attacks
    const elapsed = Date.now() - startTime;
    if (elapsed < 500) {
      await new Promise(resolve => setTimeout(resolve, 500 - elapsed));
    }

    // Always show same generic success message
    toast({
      title: t('auth.resetEmailSent'),
    });

    setResetEmail('');
    setShowForgotPassword(false);
    setLoading(false);
  };

  if (isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <div className="text-center">
          <img 
            src={logo} 
            alt="Khaylani Logo" 
            className="h-24 w-auto mx-auto mb-6 animate-pulse"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
          <LoadingSpinner size="lg" message="Aan het inloggen..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg"
      >
        Spring naar hoofdinhoud
      </a>
      
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-hero p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2djEwaC0xMFYxNmgxMHptLTIgMmgtNnY2aDZ2LTZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="relative z-10">
          <Link to="/" className="flex items-center mb-8 hover:opacity-90 transition-opacity">
            <img 
              src={logo} 
              alt="Khaylani Logo" 
              className="h-20 w-auto"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </Link>
          <p className="text-xl text-white/90 max-w-md">
            {t('auth.branding.tagline')}
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-2 gap-6">
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
            <Building2 className="h-8 w-8 text-white mb-3" />
            <h3 className="text-white font-semibold mb-1">{t('auth.feature.companies')}</h3>
            <p className="text-white/80 text-sm">{t('auth.feature.companies.desc')}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
            <Users className="h-8 w-8 text-white mb-3" />
            <h3 className="text-white font-semibold mb-1">{t('auth.feature.vacancies')}</h3>
            <p className="text-white/80 text-sm">{t('auth.feature.vacancies.desc')}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
            <TrendingUp className="h-8 w-8 text-white mb-3" />
            <h3 className="text-white font-semibold mb-1">{t('auth.feature.analytics')}</h3>
            <p className="text-white/80 text-sm">{t('auth.feature.analytics.desc')}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
            <MapPin className="h-8 w-8 text-white mb-3" />
            <h3 className="text-white font-semibold mb-1">{t('auth.feature.map')}</h3>
            <p className="text-white/80 text-sm">{t('auth.feature.map.desc')}</p>
          </div>
        </div>
      </div>

      {/* Right side - Auth Form */}
      <main id="main-content" className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center mb-4 hover:opacity-90 transition-opacity">
              <img 
                src={logo} 
                alt="Khaylani Logo" 
                className="h-15 w-auto mx-auto"
                style={{ mixBlendMode: 'multiply' }}
              />
            </Link>
          </div>

          <div className="bg-card p-8 rounded-3xl shadow-xl border border-border">
            {showForgotPassword ? (
              <>
                <h2 className="text-2xl font-bold mb-2 text-card-foreground">{t('auth.resetPasswordTitle')}</h2>
                <p className="text-muted-foreground mb-6">{t('auth.resetPasswordDescription')}</p>
                
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">{t('contact.form.email')}</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder={t('contact.form.emailPlaceholder')}
                      required
                      disabled={loading}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      t('auth.sendResetLink')
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail("");
                    }}
                    disabled={loading}
                  >
                    {t('auth.backToLogin')}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold mb-2 text-card-foreground">{t('auth.welcome')}</h2>
                <p className="text-muted-foreground mb-6">{t('auth.subtitle')}</p>
                
                <SupabaseAuth
                  supabaseClient={supabase}
                  view="sign_in"
                  showLinks={false}
                  localization={{
                    variables: {
                      sign_in: {
                        email_label: language === 'nl' ? 'E-mailadres' : 'Email address',
                        password_label: language === 'nl' ? 'Wachtwoord' : 'Password',
                        email_input_placeholder: language === 'nl' ? 'Jouw e-mailadres' : 'Your email address',
                        password_input_placeholder: language === 'nl' ? 'Jouw wachtwoord' : 'Your password',
                        button_label: language === 'nl' ? 'Inloggen' : 'Sign in',
                        loading_button_label: language === 'nl' ? 'Inloggen...' : 'Signing in ...',
                        social_provider_text: language === 'nl' ? 'Inloggen met {{provider}}' : 'Sign in with {{provider}}',
                        link_text: language === 'nl' ? 'Heb je al een account? Inloggen' : 'Already have an account? Sign in',
                      },
                    },
                  }}
                  appearance={{
                    theme: ThemeSupa,
                    variables: {
                      default: {
                        colors: {
                          brand: "hsl(217 91% 60%)",
                          brandAccent: "hsl(221 83% 53%)",
                          brandButtonText: "white",
                          defaultButtonBackground: "white",
                          defaultButtonBackgroundHover: "hsl(214 32% 91%)",
                          inputBackground: "white",
                          inputBorder: "hsl(214 32% 91%)",
                          inputBorderHover: "hsl(217 91% 60%)",
                          inputBorderFocus: "hsl(217 91% 60%)",
                        },
                        radii: {
                          borderRadiusButton: "1rem",
                          buttonBorderRadius: "1rem",
                          inputBorderRadius: "0.75rem",
                        },
                      },
                    },
                    className: {
                      container: "space-y-4",
                      button: "font-medium transition-all duration-300 hover:shadow-md",
                      input: "transition-all duration-300",
                    },
                  }}
                  providers={[]}
                  redirectTo={`${window.location.origin}/dashboard`}
                />
                
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-primary hover:underline"
                  >
                    {t('auth.forgotPassword')}
                  </button>
                </div>
              </>
            )}
          </div>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {t('auth.terms')}
          </p>
        </div>
      </main>
    </div>
  );
};

export default Auth;
